import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  integrationService,
  QrIntegrationKey,
  QrIntegrationPricing,
  QrIntegrationSubscription,
  ConfidentialIntegrationKey,
  ConfidentialIntegrationPricing,
  ConfidentialIntegrationSubscription,
} from '../services/integration.service';
import { useAuthStore } from '../store/authStore';
import { Button, Modal, ModalFooter, PageHeader, StatusBadge } from '../components/ui';

const formatAmount = (amount: number, currency?: string) => {
  if (!currency) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
};

type PurchaseChannel = 'qr' | 'confidential' | null;
type GeneratedKeyChannel = 'qr' | 'confidential' | null;

const ApiAccessDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isOperationManager = !!user?.isOperationManager;
  const canManageAccess = user?.roleLevel === 0 && !isOperationManager;

  const [qrKeys, setQrKeys] = useState<QrIntegrationKey[]>([]);
  const [qrStatus, setQrStatus] = useState<QrIntegrationSubscription | null>(null);
  const [qrPricing, setQrPricing] = useState<QrIntegrationPricing | null>(null);
  const [qrName, setQrName] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrCreating, setQrCreating] = useState(false);
  const [qrRevoking, setQrRevoking] = useState<string | null>(null);
  const [qrPurchaseLoading, setQrPurchaseLoading] = useState(false);
  const [qrCancelLoading, setQrCancelLoading] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);
  const [qrConsentAccepted, setQrConsentAccepted] = useState(false);

  const [confidentialKeys, setConfidentialKeys] = useState<ConfidentialIntegrationKey[]>([]);
  const [confidentialStatus, setConfidentialStatus] = useState<ConfidentialIntegrationSubscription | null>(null);
  const [confidentialPricing, setConfidentialPricing] = useState<ConfidentialIntegrationPricing | null>(null);
  const [confidentialName, setConfidentialName] = useState('');
  const [confidentialLoading, setConfidentialLoading] = useState(false);
  const [confidentialCreating, setConfidentialCreating] = useState(false);
  const [confidentialRevoking, setConfidentialRevoking] = useState<string | null>(null);
  const [confidentialPurchaseLoading, setConfidentialPurchaseLoading] = useState(false);
  const [confidentialCancelLoading, setConfidentialCancelLoading] = useState(false);
  const [confidentialError, setConfidentialError] = useState<string | null>(null);
  const [confidentialConsentAccepted, setConfidentialConsentAccepted] = useState(false);

  const [purchaseModal, setPurchaseModal] = useState<PurchaseChannel>(null);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [generatedKeyChannel, setGeneratedKeyChannel] = useState<GeneratedKeyChannel>(null);

  const qrActive = qrStatus?.active ?? false;
  const confidentialActive = confidentialStatus?.active ?? false;

  useEffect(() => {
    if (!user?.manufacturerId || user?.roleLevel !== 0) return;
    void loadAll();
  }, [user?.manufacturerId, user?.roleLevel, canManageAccess]);

  const loadQrData = async () => {
    setQrLoading(true);
    setQrError(null);
    try {
      const [keysResponse, pricingResponse] = await Promise.all([
        integrationService.getQrIntegrationKeys(),
        canManageAccess ? integrationService.getQrAccessPricing().catch(() => null) : Promise.resolve(null),
      ]);
      setQrKeys(keysResponse.keys || []);
      setQrStatus(keysResponse.subscription || null);
      setQrPricing(pricingResponse);
    } catch (error: any) {
      setQrError(error.response?.data?.error || 'Failed to load QR integration data');
      setQrKeys([]);
      setQrStatus(null);
      setQrPricing(null);
    } finally {
      setQrLoading(false);
    }
  };

  const loadConfidentialData = async () => {
    setConfidentialLoading(true);
    setConfidentialError(null);
    try {
      const [keysResponse, pricingResponse] = await Promise.all([
        integrationService.getConfidentialKeys(),
        canManageAccess
          ? integrationService.getConfidentialAccessPricing().catch(() => null)
          : Promise.resolve(null),
      ]);
      setConfidentialKeys(keysResponse.keys || []);
      setConfidentialStatus(keysResponse.subscription || null);
      setConfidentialPricing(pricingResponse);
    } catch (error: any) {
      setConfidentialError(error.response?.data?.error || 'Failed to load confidential integration data');
      setConfidentialKeys([]);
      setConfidentialStatus(null);
      setConfidentialPricing(null);
    } finally {
      setConfidentialLoading(false);
    }
  };

  const loadAll = async () => {
    await Promise.all([loadQrData(), loadConfidentialData()]);
  };

  const handleCreateQrKey = async () => {
    if (!canManageAccess || !qrActive) return;
    try {
      setQrCreating(true);
      setQrError(null);
      const payload = qrName.trim() ? { name: qrName.trim() } : undefined;
      const response = await integrationService.createQrIntegrationKey(payload);
      setGeneratedKey(response.key);
      setGeneratedKeyChannel('qr');
      setQrName('');
      setQrStatus(response.subscription || null);
      await loadQrData();
    } catch (error: any) {
      setQrError(error.response?.data?.error || 'Failed to create QR integration key');
    } finally {
      setQrCreating(false);
    }
  };

  const handleCreateConfidentialKey = async () => {
    if (!canManageAccess || !confidentialActive) return;
    try {
      setConfidentialCreating(true);
      setConfidentialError(null);
      const payload = confidentialName.trim() ? { name: confidentialName.trim() } : undefined;
      const response = await integrationService.createConfidentialKey(payload);
      setGeneratedKey(response.key);
      setGeneratedKeyChannel('confidential');
      setConfidentialName('');
      setConfidentialStatus(response.subscription || null);
      await loadConfidentialData();
    } catch (error: any) {
      setConfidentialError(error.response?.data?.error || 'Failed to create confidential key');
    } finally {
      setConfidentialCreating(false);
    }
  };

  const handleRevokeQrKey = async (keyId: string) => {
    if (!canManageAccess) return;
    if (!confirm('Revoke this QR integration key?')) return;
    try {
      setQrRevoking(keyId);
      setQrError(null);
      await integrationService.revokeQrIntegrationKey(keyId);
      await loadQrData();
    } catch (error: any) {
      setQrError(error.response?.data?.error || 'Failed to revoke QR integration key');
    } finally {
      setQrRevoking(null);
    }
  };

  const handleRevokeConfidentialKey = async (keyId: string) => {
    if (!canManageAccess) return;
    if (!confirm('Revoke this confidential integration key?')) return;
    try {
      setConfidentialRevoking(keyId);
      setConfidentialError(null);
      await integrationService.revokeConfidentialKey(keyId);
      await loadConfidentialData();
    } catch (error: any) {
      setConfidentialError(error.response?.data?.error || 'Failed to revoke confidential key');
    } finally {
      setConfidentialRevoking(null);
    }
  };

  const handlePurchaseQrAccess = async () => {
    if (!qrPricing || qrActive) return;
    try {
      setQrPurchaseLoading(true);
      setQrError(null);
      const result = await integrationService.purchaseQrAccess({
        consentAccepted: qrConsentAccepted,
      });
      setQrStatus({
        active: true,
        status: result.report.status,
        validUntil: result.report.validUntil,
      });
      setPurchaseModal(null);
      await loadQrData();
    } catch (error: any) {
      setQrError(error.response?.data?.error || error.message || 'Failed to purchase QR integration access');
    } finally {
      setQrPurchaseLoading(false);
    }
  };

  const handlePurchaseConfidentialAccess = async () => {
    if (!confidentialPricing || confidentialActive) return;
    try {
      setConfidentialPurchaseLoading(true);
      setConfidentialError(null);
      const result = await integrationService.purchaseConfidentialAccess({
        consentAccepted: confidentialConsentAccepted,
      });
      setConfidentialStatus({
        active: true,
        status: result.report.status,
        validUntil: result.report.validUntil,
      });
      setPurchaseModal(null);
      await loadConfidentialData();
    } catch (error: any) {
      setConfidentialError(
        error.response?.data?.error || error.message || 'Failed to purchase confidential premium access'
      );
    } finally {
      setConfidentialPurchaseLoading(false);
    }
  };

  const handleCancelQrAccess = async () => {
    if (!qrActive || !canManageAccess) return;
    if (!confirm('Cancel QR integration subscription? Access remains until the current period ends.')) return;
    try {
      setQrCancelLoading(true);
      setQrError(null);
      const result = await integrationService.cancelQrAccess();
      if (result.report) {
        setQrStatus({
          active: true,
          status: result.report.status,
          validUntil: result.report.validUntil,
        });
      }
    } catch (error: any) {
      setQrError(error.response?.data?.error || error.message || 'Failed to cancel QR integration access');
    } finally {
      setQrCancelLoading(false);
    }
  };

  const handleCancelConfidentialAccess = async () => {
    if (!confidentialActive || !canManageAccess) return;
    if (
      !confirm(
        'Cancel confidential premium subscription? Existing releases remain until their expiry, but new access features are locked after period end.'
      )
    ) {
      return;
    }
    try {
      setConfidentialCancelLoading(true);
      setConfidentialError(null);
      const result = await integrationService.cancelConfidentialAccess();
      if (result.report) {
        setConfidentialStatus({
          active: true,
          status: result.report.status,
          validUntil: result.report.validUntil,
        });
      }
    } catch (error: any) {
      setConfidentialError(
        error.response?.data?.error || error.message || 'Failed to cancel confidential premium access'
      );
    } finally {
      setConfidentialCancelLoading(false);
    }
  };

  const handleCopyGeneratedKey = async () => {
    if (!generatedKey) return;
    try {
      await navigator.clipboard.writeText(generatedKey);
      alert('API key copied to clipboard.');
    } catch {
      alert('Copy failed. Please copy the key manually.');
    }
  };

  const purchaseModalTitle = useMemo(() => {
    if (purchaseModal === 'confidential') return 'Unlock Confidential Premium';
    if (purchaseModal === 'qr') return 'Unlock QR Integration';
    return 'Unlock Access';
  }, [purchaseModal]);

  const purchaseLoading =
    purchaseModal === 'qr' ? qrPurchaseLoading : purchaseModal === 'confidential' ? confidentialPurchaseLoading : false;

  const consentAccepted =
    purchaseModal === 'qr'
      ? qrConsentAccepted
      : purchaseModal === 'confidential'
        ? confidentialConsentAccepted
        : false;

  const pricing =
    purchaseModal === 'qr'
      ? qrPricing
      : purchaseModal === 'confidential'
        ? confidentialPricing
        : null;

  const handleConfirmPurchase = async () => {
    if (purchaseModal === 'qr') {
      await handlePurchaseQrAccess();
      return;
    }
    if (purchaseModal === 'confidential') {
      await handlePurchaseConfidentialAccess();
    }
  };

  const setModalConsent = (checked: boolean) => {
    if (purchaseModal === 'qr') {
      setQrConsentAccepted(checked);
      return;
    }
    if (purchaseModal === 'confidential') {
      setConfidentialConsentAccepted(checked);
    }
  };

  const renderKeyTable = (
    keys: IntegrationKeyRow[],
    canManage: boolean,
    revokingId: string | null,
    onRevoke: (id: string) => void
  ) => {
    if (keys.length === 0) {
      return <p className="text-sm text-gray-500">No API keys created yet.</p>;
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm" aria-label="API keys">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-semibold text-gray-500">Label</th>
              <th scope="col" className="px-3 py-2 text-left font-semibold text-gray-500">Key</th>
              <th scope="col" className="px-3 py-2 text-left font-semibold text-gray-500">Status</th>
              <th scope="col" className="px-3 py-2 text-left font-semibold text-gray-500">Created</th>
              <th scope="col" className="px-3 py-2 text-left font-semibold text-gray-500">Last used</th>
              <th scope="col" className="px-3 py-2 text-right font-semibold text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {keys.map((key) => (
              <tr key={key.id}>
                <td className="px-3 py-2 text-gray-900">{key.name || 'Untitled'}</td>
                <td className="px-3 py-2 text-gray-700 font-mono">{key.maskedKey}</td>
                <td className="px-3 py-2">
                  <StatusBadge tone={key.isActive ? 'active' : 'disabled'} size="sm">
                    {key.isActive ? 'Active' : 'Revoked'}
                  </StatusBadge>
                </td>
                <td className="px-3 py-2 text-gray-600">
                  {new Date(key.createdAt).toLocaleDateString()}
                </td>
                <td className="px-3 py-2 text-gray-600">
                  {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : '-'}
                </td>
                <td className="px-3 py-2 text-right">
                  {key.isActive && canManage ? (
                    <button
                      onClick={() => onRevoke(key.id)}
                      disabled={revokingId === key.id}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      {revokingId === key.id ? 'Revoking...' : 'Revoke'}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <PageHeader
        title="API Access"
        description="Manage integration subscriptions and API keys for partner verification and confidential sharing."
        actions={
          <Button variant="outline" onClick={() => navigate('/settings')}>
            Back to Settings
          </Button>
        }
      />

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">QR Verification API</h2>
            <p className="text-sm text-gray-600">
              Generate keys for partner apps that need standard QR verification responses.
            </p>
            {qrStatus?.validUntil && (
              <p className="text-xs text-gray-500 mt-1">
                Access valid until {new Date(qrStatus.validUntil).toLocaleDateString()}
              </p>
            )}
            {qrError && <p className="text-xs text-red-600 mt-1">{qrError}</p>}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <StatusBadge tone={qrActive ? 'active' : 'disabled'}>
              {qrActive ? 'Active' : 'Locked'}
            </StatusBadge>
            {qrActive && canManageAccess && (
              <button
                onClick={handleCancelQrAccess}
                disabled={qrCancelLoading}
                className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                  qrCancelLoading
                    ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {qrCancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            )}
            {!qrActive && canManageAccess && (
              <button
                onClick={() => {
                  setQrConsentAccepted(false);
                  setPurchaseModal('qr');
                }}
                disabled={!qrPricing}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  !qrPricing
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {qrPricing ? `Unlock for ${qrPricing.formatted}` : 'Pricing unavailable'}
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              value={qrName}
              onChange={(e) => setQrName(e.target.value)}
              placeholder="Key label (optional)"
              disabled={!canManageAccess || !qrActive}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <button
              onClick={handleCreateQrKey}
              disabled={!canManageAccess || !qrActive || qrCreating}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                !canManageAccess || !qrActive || qrCreating
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-900 text-white hover:bg-black'
              }`}
            >
              {qrCreating ? 'Creating...' : 'Generate API Key'}
            </button>
          </div>
          {qrLoading ? <p className="text-sm text-gray-500">Loading keys...</p> : renderKeyTable(qrKeys, canManageAccess, qrRevoking, handleRevokeQrKey)}
          <p className="text-xs text-gray-500">
            Endpoint: <span className="font-mono">/api/qr/verify-partner</span> with <span className="font-mono">X-Api-Key</span>.
          </p>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Confidential Info API</h2>
            <p className="text-sm text-gray-600">
              Premium channel to expose retailer/dealer-only instructions, promo codes, and discounts from QR.
            </p>
            {confidentialStatus?.validUntil && (
              <p className="text-xs text-gray-500 mt-1">
                Access valid until {new Date(confidentialStatus.validUntil).toLocaleDateString()}
              </p>
            )}
            {confidentialError && <p className="text-xs text-red-600 mt-1">{confidentialError}</p>}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <StatusBadge tone={confidentialActive ? 'active' : 'disabled'}>
              {confidentialActive ? 'Premium Active' : 'Premium Locked'}
            </StatusBadge>
            {confidentialActive && canManageAccess && (
              <button
                onClick={handleCancelConfidentialAccess}
                disabled={confidentialCancelLoading}
                className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                  confidentialCancelLoading
                    ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {confidentialCancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            )}
            {!confidentialActive && canManageAccess && (
              <button
                onClick={() => {
                  setConfidentialConsentAccepted(false);
                  setPurchaseModal('confidential');
                }}
                disabled={!confidentialPricing}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  !confidentialPricing
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                {confidentialPricing
                  ? `Unlock for ${confidentialPricing.formatted}`
                  : 'Pricing unavailable'}
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              value={confidentialName}
              onChange={(e) => setConfidentialName(e.target.value)}
              placeholder="Confidential key label (optional)"
              disabled={!canManageAccess || !confidentialActive}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <button
              onClick={handleCreateConfidentialKey}
              disabled={!canManageAccess || !confidentialActive || confidentialCreating}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                !canManageAccess || !confidentialActive || confidentialCreating
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-900 text-white hover:bg-black'
              }`}
            >
              {confidentialCreating ? 'Creating...' : 'Generate Confidential Key'}
            </button>
          </div>
          {confidentialLoading ? (
            <p className="text-sm text-gray-500">Loading keys...</p>
          ) : (
            renderKeyTable(confidentialKeys, canManageAccess, confidentialRevoking, handleRevokeConfidentialKey)
          )}
          <p className="text-xs text-gray-500">
            Endpoint: <span className="font-mono">/api/qr/verify-confidential</span> with <span className="font-mono">X-Api-Key</span> and body field <span className="font-mono">viewer=retailer|dealer</span>.
          </p>
        </div>
      </section>

      <Modal
        isOpen={purchaseModal !== null}
        onClose={() => setPurchaseModal(null)}
        title={purchaseModalTitle}
        size="md"
      >
        <div className="space-y-4 text-sm text-gray-700">
          <p>
            {purchaseModal === 'confidential'
              ? 'Enable premium confidential release sharing. This is required to create confidential keys and reveal batch-level retailer/dealer data through QR.'
              : 'Enable QR integration for partner apps. Base access covers the initial period, then auto-charges monthly until cancellation.'}
          </p>
          <div className="rounded-lg bg-gray-50 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Price</span>
              <span className="font-semibold text-gray-900">
                {pricing ? formatAmount(pricing.amount, pricing.currency) : 'Pricing unavailable'}
              </span>
            </div>
            {pricing?.baseCoverageMonths !== undefined && (
              <p className="text-xs text-gray-500">
                Base coverage: {pricing.baseCoverageMonths} month{pricing.baseCoverageMonths === 1 ? '' : 's'}
              </p>
            )}
            {pricing?.monthlySurchargePercent !== undefined && pricing.monthlySurchargePercent > 0 && (
              <p className="text-xs text-gray-500">
                Monthly surcharge after coverage: +{pricing.monthlySurchargePercent}%
              </p>
            )}
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setModalConsent(e.target.checked)}
              aria-label="Consent to subscription charges"
            />
            <span>{pricing?.consentText || 'I consent to subscription charges for this feature.'}</span>
          </label>
        </div>
        <ModalFooter>
          <button
            onClick={() => setPurchaseModal(null)}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
            disabled={purchaseLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmPurchase}
            disabled={!consentAccepted || purchaseLoading || !pricing}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              !consentAccepted || purchaseLoading || !pricing
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {purchaseLoading ? 'Processing...' : 'Confirm and Pay'}
          </button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!generatedKey}
        onClose={() => {
          setGeneratedKey(null);
          setGeneratedKeyChannel(null);
        }}
        title={generatedKeyChannel === 'confidential' ? 'Confidential Key Created' : 'API Key Created'}
        size="md"
      >
        <div className="space-y-3 text-sm text-gray-700">
          <p>Copy this key now. You will not be able to view it again.</p>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs break-all">
            {generatedKey}
          </div>
        </div>
        <ModalFooter>
          <button
            onClick={() => {
              setGeneratedKey(null);
              setGeneratedKeyChannel(null);
            }}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
          >
            Close
          </button>
          <button
            onClick={handleCopyGeneratedKey}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-black"
          >
            Copy Key
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

type IntegrationKeyRow = {
  id: string;
  name?: string;
  maskedKey: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt?: string | null;
};

export default ApiAccessDashboard;
